// @flow
import { trim, isBlank } from "voca";
import consts from "common/consts";
import { EVENT_TYPES, consts as uploaderConsts } from "uploader";
import getLogger from "common/logger";
import { asAbsolute } from "root/routes/utils";
import { types as ats } from "root/actions";
import getThrottledSetQueueItems from "./throttledSetQueueItems";

type propsType = {
	location: { pathname: string },
	uploader: Object,
	children: Object,
	containerData: {
		uploadAdded?: { files: Object[], cropInfo: Object[] },
		queueIds: string[],
		retryIds?: string[],
		abortIds?: string[],
		closeCounter: number,
		widgetViewType: number,
		uploadOptions: {
			publicId?: string,
			tags?: string[],
			tagsInputValue: string,
			uploadPreset?: string,
		},
		metadataInfo: {
			metadataResult: Object,
			conflicts: Object[],
			uploadFiles: Object[],
			cropInfo: Object[],
		},
	},
	hasActiveUploads: boolean,
	showAdvancedOptions: boolean,
	[string]: Function
};

type showDataType = { source: string, options?: { fileDialog?: boolean } }

const logger = getLogger();

const BATCH_CANCEL_ERRORS_MAP = {
	[uploaderConsts.BATCH_CANCEL_REASONS.MAX_EXCEEDED]: ats.SET_MAX_SELECT_REACHED,
	[uploaderConsts.BATCH_CANCEL_REASONS.INVALID_PUBLIC_ID]: ats.SET_INVALID_PUBLIC_ID_FOUND,
};

export default (options: propsType, router: Object): { updateProps: Function, processNextProps: Function } => {
	let containerProps;
	let uploader;
	let containerData;
	let throttledSetQueueItems;

	const updateConfig = (config?: Object) => {
		const configObj = config || uploader.getConfig();
		containerProps[ats.SET_WIDGET_CONFIG](configObj);
	};

	const isInPath = (path: string, props?: propsType) => {
		const loc = props ? props.location : containerProps.location;
		return !!(~loc.pathname.indexOf(path));
	};
	const clearView = () => {
		containerProps[ats.SET_ADVANCED_OPTIONS](false);
	};
	const navigateToPath = (path: string) => {
		const navigating = !isInPath(path);

		if (navigating) { // only navigate if not already there
			router.history.push({
				...containerProps.location,
				pathname: asAbsolute(path),
			});
			clearView();
		}

		return navigating;
	};

	const getPreQueuePath = () => {
		const { history } = router;
		const index = (history.index - 1); // index-1 coz we've already navigated to queue

		let path = history.entries[index];

		if (path && (~path.pathname.indexOf("crop") || ~path.pathname.indexOf("queue"))) {
			path = index ? history.entries[index - 1] : null;
		}

		return path;
	};

	const navigateToQueue = () => {
		const navigated = navigateToPath(consts.QUEUE); // navigate to queue view

		if (navigated) {
			containerProps[ats.SET_LAST_PRE_QUEUE_PATH]({ path: getPreQueuePath() });
		}
	};

	const navigateToCrop = () => navigateToPath(consts.CROP); // navigate to crop view

	const setWidgetViewType = (viewType: number) => {
		logger.log(`[UploadContainer.connector]: widget view type changed to: ${viewType}`);
		uploader.setWidgetViewType(viewType);
	};

	const navigateToTab = (data: { source: string }) => {
		let path = null;

		if (data && data.source) {
			path = data.source;
		}

		if (path) {
			navigateToPath(path); // go to the tab requested
		} else if (isInPath(consts.QUEUE)) {
			containerProps[ats.DO_NAV_TO_PREVIOUS_PATH](router);
		}
	};

	const dispatchClearAdvanced = () => {
		if (containerProps.showAdvancedOptions) {
			containerProps[ats.CLEAR_ADVANCED_VALUES]();
		}
	};

	const reportCropSources = (src, data = {}) => containerProps[ats.SET_UPLOADER_CROP_SOURCES]({
		src,
		data: { files: data.files || [] }
	});

	const eventHandlers = {
		[EVENT_TYPES.UPLOADER_READY]() {
			logger.log("[UploadContainer.connector]: uploader reported that its now ready");
			updateConfig();
			dispatchShowing();
		},

		[EVENT_TYPES.UPLOADER_FILE_STATUS](data: { ids: string[], files: Object, simulated?: boolean }) {
			logger.log("[UploadContainer.connector]: upload queue reported files added/changed, updating reducer queue");
			throttledSetQueueItems(data.ids, data.files, data.simulated);
		},

		[EVENT_TYPES.UPLOADER_SHOW](data: showDataType) {
			logger.log("[UploadContainer.connector]: received uploader is showing", data);
			containerProps[ats.SET_WIDGET_VIEW_TYPE]({ type: consts.WIDGET_VIEW_TYPES.INITIAL });
			navigateToTab(data);
			containerProps[ats.SET_WIDGET_SHOWING]();
		},

		[EVENT_TYPES.UPLOADER_UPDATE_SETTINGS](config: Object) {
			logger.log("[UploadContainer.connector]: received uploader settings update !");
			updateConfig(config);
		},

		[EVENT_TYPES.UPLOADER_CLIENT_HIDE]() {
			logger.log("[UploadContainer.connector]: received uploader was hidden by client");
			containerProps[ats.DO_CLOSE_WIDGET]();
		},

		[EVENT_TYPES.UPLOADER_CLIENT_TAGS](data: { tags: string[] }) {
			logger.log("[UploadContainer.connector]: received client tags", data);
			containerProps[ats.SET_SUGGESTED_TAGS](data);
		},

		[EVENT_TYPES.UPLOADER_CLIENT_UPLOAD_PRESETS](data: { uploadPresets: string[] }) {
			logger.log("[UploadContainer.connector]: received client upload presets", data);
			containerProps[ats.SET_SUGGESTED_UPLOAD_PRESETS](data);
		},

		[EVENT_TYPES.UPLOADER_CLIENT_MINIMIZE]() {
			logger.log("[UploadContainer.connector]: received client minimize");
			navigateToQueue();
			containerProps[ats.SET_WIDGET_VIEW_TYPE]({ type: consts.WIDGET_VIEW_TYPES.MINI });
		},

		[EVENT_TYPES.UPLOADER_BATCH_STARTED]() {
			logger.log("[UploadContainer.connector]: received batch added");
			if (!isInPath(consts.QUEUE)) {
				navigateToQueue();
			}

			options[ats.SET_BATCH_STARTED]();
			containerProps[ats.SET_PUBLIC_ID]({ value: ""});
		},

		[EVENT_TYPES.UPLOADER_CROP_STARTED](data: Object) {
			logger.log("[UploadContainer.connector]: received crop started", data);

			reportCropSources(""); // reset crop sources before we start
			navigateToCrop();

			// todo: use utils/fileReader
			const isCustomUpload = data.files[0]._custom;

			if (typeof data.files[0] === "string" || isCustomUpload) {
				// need to use the file prop for camera and custom upload (ex: url from client open())
				reportCropSources(isCustomUpload ? (data.files[0].objectURL || data.files[0].file) : data.files[0], data);
			} else {
				const fr = new FileReader();

				try {
					const file = data.files[0]?.binary || data.files[0];
					fr.readAsDataURL(file);
				} catch (ex) {
					logger.devError("[UploadContainer.connector]: failed to read file url for crop");
					containerProps[ats.SET_CROP]({ imageError: true });
				}

				fr.onload = () => reportCropSources(fr.result, data);
			}
		},

		[EVENT_TYPES.UPLOADER_BATCH_CANCELLED](data: { reason: string, info?: { files: Object[] } }) {
			logger.log(`[UploadContainer.connector]: received batch cancelled with reason: ${data.reason}`);

			containerProps[ats.SET_WIDGET_VIEW_TYPE]({
				type: consts.WIDGET_VIEW_TYPES.INITIAL,
				router,
			});

			if (data.info && data.info.files) {
				containerProps[ats.CLEAR_CANCELLED_UPLOADS](data.info); // remove cancelled uploads from state so they can be added again
			}

			const notificationType = BATCH_CANCEL_ERRORS_MAP[data.reason];

			if (notificationType) {
				containerProps[notificationType]();
			}
		},

		[EVENT_TYPES.UPLOADER_CLOUD_INFO](data: Object) {
			logger.log("[UploadContainer.connector]: received cloud info ", data);
			options[ats.SET_IS_PREMIUM](data);
		},

		[EVENT_TYPES.UPLOADER_SUCCESS_COUNTER](data: { count: number }) {
			logger.log("[UploadContainer.connector]: received updated success counter ", data);
			options[ats.SET_SUCCESSFUL_COUNTER](data);
		},

		[EVENT_TYPES.UPLOADER_MD_STARTED](schemaData){
			logger.log("[UploadContainer.connector]: received metadata flow started", schemaData);

			containerProps[ats.SET_METADATA_SCHEMA_DATA]({data: schemaData});
			const navigated = navigateToPath(consts.METADATA);

			if (navigated) {
				containerProps[ats.SET_LAST_PRE_QUEUE_PATH]({ path: getPreQueuePath() });
			}
		},
	};

	const registerEvents = () => {
		Object.keys(eventHandlers).forEach((type) => uploader.on(type, eventHandlers[type]));
	};

	const dispatchShowing = () => {
		if (uploader.isShowingOnStart()) {
			containerProps[ats.SET_WIDGET_SHOWING]();
		}
	};

	const setUploadOptions = (nextProps: propsType) => {
		const nextUpOptions = nextProps.containerData.uploadOptions;

		if (nextUpOptions) {
			const previousTags = containerData.uploadOptions.tags;
			const previousUploadPreset = containerData.uploadOptions.uploadPreset;

			let uploadTags = (nextUpOptions.tags !== previousTags) ?
				nextUpOptions.tags : previousTags;

			const uploadPreset = (nextUpOptions.uploadPreset !== previousUploadPreset) ?
				nextUpOptions.uploadPreset : previousUploadPreset;

			// add the last tag input even in case of user not confirming it as a tag
			if (nextUpOptions.tagsInputValue !== containerData.uploadOptions.tagsInputValue) {
				uploadTags = [].concat(uploadTags,
					!isBlank(nextUpOptions.tagsInputValue) ?
						trim(nextUpOptions.tagsInputValue) :
						[]); // in case value is empty, pass empty array so uploader is updated with no tags
			}

			if (uploadTags !== previousTags) {
				uploader.setUploadTags(uploadTags);
			}

			if (nextUpOptions.publicId !== containerData.uploadOptions.publicId) {
				uploader.setUploadPublicId(nextUpOptions.publicId);
			}

			if (uploadPreset !== previousUploadPreset) {
				uploader.setUploadPreset(uploadPreset);
			}
		}
	};

	const processRetries = (nextProps: propsType) => {
		if (nextProps.retryIds && nextProps.retryIds.length
			&& nextProps.retryIds !== containerProps.retryIds) {
			logger.log("[UploadContainer.connector]: about to initiate retry for failed item(s): ", nextProps.retryIds);
			uploader.retryUploads(nextProps.retryIds);
		}
	};

	const processAborts = (nextProps: propsType) => {
		if (nextProps.abortIds && nextProps.abortIds.length
			&& nextProps.abortIds !== containerProps.abortIds) {
			logger.log("[UploadContainer.connector]: about to initiate abort for item(s): ", nextProps.abortIds);
			uploader.abortUploads(nextProps.abortIds);
		}
	};

	const filterOutPreviouslyAddedCropData = (prev: propsType, next: propsType) => {
		const prevCropData = prev.containerData.uploadAdded && prev.containerData.uploadAdded.cropInfo ?
			prev.containerData.uploadAdded.cropInfo : [];
		const nextCropData = next.containerData.uploadAdded && next.containerData.uploadAdded.cropInfo ?
			next.containerData.uploadAdded.cropInfo : [];

		return prevCropData.length && nextCropData.length ?
			nextCropData.filter((f) => !~prevCropData.indexOf(f)) :
			nextCropData;
	};

	const filterOutPreviouslyAdded = (prev: propsType, next: propsType) => {
		const prevFiles = prev.containerData.uploadAdded ? prev.containerData.uploadAdded.files : [];
		const nextFiles = next.containerData.uploadAdded ? next.containerData.uploadAdded.files : [];

		return prevFiles.length && nextFiles.length ?
			nextFiles.filter((f,i) => prevFiles[i] !== f) :
			nextFiles;
	};

	const getAddedFromCrop = (addedCrop, next: propsType) => {
		const files = [];
		const nextFiles = next.containerData.uploadAdded ? next.containerData.uploadAdded.files : [];

		addedCrop.forEach((crop) => {
			files.push(nextFiles[nextFiles.indexOf(crop.file)]);
		});

		return files;
	};

	const processUploadsAdded = (nextProps: propsType) => {
		if (nextProps.containerData.uploadAdded) {
			let added = filterOutPreviouslyAdded(containerProps, nextProps);
			const addedCrop = filterOutPreviouslyAddedCropData(containerProps, nextProps);

			if (addedCrop.length && !added.length) {
				added = getAddedFromCrop(addedCrop, nextProps);
			}

			if (added.length) {
				logger.log("[UploadContainer.connector]: new upload items added, sending to queue", added);
				uploader.addToQueue({
					files: added,
					cropInfo: addedCrop,
				});
			}
		}
	};

	const processMetadataInfo = (nextProps: propsType) => {
		if (nextProps.containerData.metadataInfo && !containerProps.containerData.metadataInfo) {
			logger.log("[UploadContainer.connector]: metadata values received", nextProps.containerData.metadataInfo);
			const { values, fields, uploadFiles, cropInfo, conflicts } = nextProps.containerData.metadataInfo;

			uploader.addToQueue({
				files: uploadFiles,
				metadata: values,
				fields,
				conflicts,
				cropInfo,
			});
		}
	};

	const processCloseCounter = async (nextProps: propsType) => {
		if (nextProps.containerData.closeCounter
			&& nextProps.containerData.closeCounter > containerData.closeCounter) {
			logger.log("[UploadContainer.connector]: received new close counter, clearing uploader queue");

			// queue not showing and there are still active uploads - show minimized queue instead of closing
			if (!isInPath(consts.QUEUE, nextProps) && nextProps.hasActiveUploads) {
				containerProps[ats.SET_WIDGET_VIEW_TYPE]({ type: consts.WIDGET_VIEW_TYPES.MINI });
				navigateToQueue();
				setWidgetViewType(consts.WIDGET_VIEW_TYPES.MINI);
			} else {
				const clearResult = await uploader.clearQueues();

				if (clearResult) {
					logger.log("[UploadContainer.connector]: clear queues finished successfully - sending hide command to uploader");
					dispatchClearAdvanced(); // clear advanced values (id, tags)
					containerProps[ats.CLEAR_QUEUE]();
					containerProps[ats.CLEAR_ALL_NOTIFICATIONS]();
					uploader.hide();
				}
			}
		}
	};

	const processWidgetViewType = (nextProps: propsType) => {
		if (nextProps.containerData.widgetViewType
			&& nextProps.containerData.widgetViewType !== containerData.widgetViewType) {
			setWidgetViewType(nextProps.containerData.widgetViewType);
		}
	};

	const init = () => {
		updateProps(options);
		registerEvents();
	};

	const processNextProps = (nextProps: propsType) => Promise.all( // returning promise.all for async testing purposes
		[processUploadsAdded(nextProps),
			processRetries(nextProps),
			processAborts(nextProps),
			setUploadOptions(nextProps),
			processCloseCounter(nextProps),
			processWidgetViewType(nextProps),
			processMetadataInfo(nextProps),
		]
	);

	const updateProps = (props: propsType) => {
		containerProps = props;
		uploader = props.uploader;
		containerData = props.containerData;

		throttledSetQueueItems = getThrottledSetQueueItems(containerProps[ats.SET_QUEUE_ITEMS]);
	};

	init();

	return {
		updateProps,
		processNextProps,
	};
};
